import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
const PARTNER_TYPES = [
  { value: "coach", label: "Coach" },
  { value: "academy", label: "Academy" },
  { value: "creator", label: "Creator" },
  { value: "influencer", label: "Influencer" },
  { value: "club", label: "Club" },
  { value: "school", label: "School" },
  { value: "league", label: "League" },
  { value: "other", label: "Other" },
];

const SOCIAL_OPTIONS = [
  { value: "instagram", label: "Instagram" },
  { value: "youtube", label: "YouTube" },
  { value: "tiktok", label: "TikTok" },
  { value: "facebook", label: "Facebook" },
  { value: "x", label: "X (Twitter)" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "website", label: "Website" },
  { value: "booking", label: "Booking link" },
  { value: "google_maps", label: "Google Maps" },
  { value: "custom", label: "Custom" },
];

const DEFAULT_BIO = "Helping cricketers improve between sessions. Try Gully through my link.";

/**
 * Iter XX.43 — Claim your Gully partner profile.
 * 2-minute wizard: choose type → pick username → add details → publish.
 */
export default function PartnerClaim() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(0);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Iter XX.46 — accept `?as=type` deep-link so the Earn hub can seed
  // the wizard with the persona the visitor picked.
  const initialType = (() => {
    const as = (searchParams.get("as") || "").toLowerCase();
    const known = PARTNER_TYPES.some(t => t.value === as);
    return known ? as : "coach";
  })();

  const [partnerType, setPartnerType] = useState(initialType);
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, msg: "" });
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState(DEFAULT_BIO);
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [socialLinks, setSocialLinks] = useState([]);
  // Iter XX.45 — Partner Agreement acceptance gate.
  const [agreement, setAgreement] = useState(null);
  const [acceptedAgreement, setAcceptedAgreement] = useState(false);

  // Load the active Partner Agreement once.
  useEffect(() => {
    let alive = true;
    api.get("/policies/partner_agreement")
      .then(r => { if (alive) setAgreement(r.data); })
      .catch(() => { /* fatal: publish will fail with a clear 409 */ });
    return () => { alive = false; };
  }, []);

  // Load existing partner profile.
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get("/partners/me");
        if (alive) {
          setExisting(data.partner || null);
          if (data.partner) setDisplayName(user?.name || "");
          setLoading(false);
        }
      } catch { if (alive) setLoading(false); }
    }
    load();
    return () => { alive = false; };
  }, [authLoading, user]);

  // Debounced username availability check.
  useEffect(() => {
    if (!username) { setUsernameStatus({ checking: false, available: null, msg: "" }); return; }
    const clean = username.replace(/^@/, "").toLowerCase();
    if (clean.length < 3) {
      setUsernameStatus({ checking: false, available: false, msg: "At least 3 characters" });
      return;
    }
    if (!/^[a-z0-9_.]+$/.test(clean)) {
      setUsernameStatus({ checking: false, available: false, msg: "Only letters, numbers, underscore, period" });
      return;
    }
    setUsernameStatus({ checking: true, available: null, msg: "Checking…" });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/partners/username-available", { params: { u: clean } });
        setUsernameStatus({
          checking: false,
          available: data.available,
          msg: data.available ? `@${clean} is available` : `@${clean} is taken or reserved`,
        });
      } catch {
        setUsernameStatus({ checking: false, available: null, msg: "" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [username]);

  async function handlePublish() {
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        partner_type: partnerType,
        username: username.replace(/^@/, "").toLowerCase(),
        display_name: displayName.trim() || user?.name || "Gully partner",
        bio: bio.trim() || DEFAULT_BIO,
        city: city.trim() || null,
        country: country.trim() || null,
        social_links: socialLinks.filter(l => l.platform && l.url),
        primary_cta_type: "gully",
        publish_immediately: true,
        // Iter XX.45 — Partner Agreement acceptance is required before
        // publish. We include the active version here so the backend
        // rejects with a clear 409 if the client is out of date.
        accepted_agreement_version: agreement?.version || null,
      };
      const { data: created } = await api.post("/partners", body);
      const uname = created?.partner?.username || body.username;
      toast.success(`Your Gully page is live at askgully.com/@${uname}`);
      // Small delay so the toast is visible before navigation.
      setTimeout(() => navigate("/partner"), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create profile");
      setSaving(false);
    }
  }

  if (authLoading || loading) {
    return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">Loading…</div>;
  }
  if (!user) {
    navigate("/login?return_to=/partner/claim");
    return null;
  }
  if (existing) {
    // Already has a profile — offer to view/edit.
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-6" data-testid="partner-claim-existing">
        <div className="max-w-md w-full text-center">
          <div className="text-2xl font-semibold mb-2">You already have a Gully page</div>
          <div className="text-neutral-400 mb-6">
            <span className="text-emerald-400" data-testid="existing-username">@{existing.username}</span> · {existing.status}
          </div>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/partner")} className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950" data-testid="go-to-partner-dashboard">
              Go to Partner Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate(`/@${existing.username}`)} className="border-neutral-700" data-testid="view-public-partner-page">
              View public page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const canPublish = usernameStatus.available === true
                      && displayName.trim().length > 0
                      && acceptedAgreement
                      && !!agreement?.version;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="partner-claim">
      <div className="max-w-md mx-auto px-6 py-10">
        <button
          onClick={() => navigate("/me")}
          className="text-sm text-neutral-500 hover:text-neutral-300 mb-6"
          data-testid="partner-claim-back"
        >← Back</button>
        <h1 className="text-3xl font-semibold mb-2" data-testid="partner-claim-headline">
          {(() => {
            const map = {
              coach:      "Invite your players.",
              academy:    "Grow your academy.",
              creator:    "Tell your followers.",
              influencer: "Turn your audience into earnings.",
              club:       "Grow your club.",
              school:     "Empower your students.",
              league:     "Grow your community.",
              other:      "Get your Gully link.",
            };
            return map[partnerType] || "Get your Gully link.";
          })()}
        </h1>
        <p className="text-neutral-400 mb-8" data-testid="partner-claim-subhead">
          Two-minute setup. You get a permanent page, a referral link, and a wallet — all free to claim.
        </p>

        {/* Partner type */}
        <label className="text-sm text-neutral-400 mb-2 block">I am a…</label>
        <div className="grid grid-cols-2 gap-2 mb-6" data-testid="partner-type-picker">
          {PARTNER_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => setPartnerType(t.value)}
              className={`px-3 py-2 rounded-lg border text-sm text-left transition ${
                partnerType === t.value
                  ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                  : "border-neutral-800 hover:border-neutral-700 text-neutral-300"
              }`}
              data-testid={`partner-type-${t.value}`}
            >{t.label}</button>
          ))}
        </div>

        {/* Username */}
        <label className="text-sm text-neutral-400 mb-2 block">Your Gully handle</label>
        <div className="flex items-stretch gap-0 rounded-lg overflow-hidden border border-neutral-800 mb-1 focus-within:border-emerald-500">
          <div className="px-3 flex items-center bg-neutral-900 text-neutral-500 text-sm">askgully.com/@</div>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/^@/, "").toLowerCase())}
            placeholder="yourhandle"
            className="flex-1 bg-neutral-900 border-0 rounded-none focus-visible:ring-0 text-emerald-300"
            data-testid="partner-username-input"
          />
        </div>
        <div className={`text-xs mb-6 h-4 ${
          usernameStatus.available === true ? "text-emerald-400" :
          usernameStatus.available === false ? "text-red-400" : "text-neutral-500"
        }`} data-testid="partner-username-status">{usernameStatus.msg}</div>

        {/* Display name */}
        <label className="text-sm text-neutral-400 mb-2 block">Display name</label>
        <Input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="How you want to be shown"
          className="bg-neutral-900 border-neutral-800 mb-6 text-neutral-100"
          data-testid="partner-display-name-input"
        />

        {/* Bio */}
        <label className="text-sm text-neutral-400 mb-2 block">Bio</label>
        <Textarea
          value={bio}
          onChange={(e) => setBio(e.target.value.slice(0, 600))}
          rows={3}
          className="bg-neutral-900 border-neutral-800 mb-1 text-neutral-100"
          data-testid="partner-bio-input"
        />
        <div className="text-xs text-neutral-500 mb-6 text-right">{bio.length}/600</div>

        {/* Location */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div>
            <label className="text-sm text-neutral-400 mb-2 block">City</label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} className="bg-neutral-900 border-neutral-800 text-neutral-100" data-testid="partner-city-input" />
          </div>
          <div>
            <label className="text-sm text-neutral-400 mb-2 block">Country</label>
            <Input value={country} onChange={(e) => setCountry(e.target.value)} className="bg-neutral-900 border-neutral-800 text-neutral-100" data-testid="partner-country-input" />
          </div>
        </div>

        {/* Social links */}
        <label className="text-sm text-neutral-400 mb-2 block">Social links (optional)</label>
        <div className="space-y-2 mb-4" data-testid="partner-social-links-editor">
          {socialLinks.map((link, idx) => (
            <div key={idx} className="flex gap-2">
              <select
                value={link.platform}
                onChange={(e) => {
                  const next = [...socialLinks]; next[idx] = { ...next[idx], platform: e.target.value }; setSocialLinks(next);
                }}
                className="bg-neutral-900 border border-neutral-800 rounded px-2 text-sm text-neutral-100"
                data-testid={`partner-social-platform-${idx}`}
              >
                {SOCIAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <Input
                value={link.url}
                onChange={(e) => {
                  const next = [...socialLinks]; next[idx] = { ...next[idx], url: e.target.value }; setSocialLinks(next);
                }}
                placeholder="https://…"
                className="flex-1 bg-neutral-900 border-neutral-800 text-neutral-100"
                data-testid={`partner-social-url-${idx}`}
              />
              <button
                onClick={() => setSocialLinks(socialLinks.filter((_, i) => i !== idx))}
                className="px-3 text-neutral-500 hover:text-red-400"
                aria-label="Remove"
              >×</button>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          onClick={() => setSocialLinks([...socialLinks, { platform: "instagram", url: "" }])}
          className="w-full mb-6 border-neutral-800 text-neutral-400"
          data-testid="partner-add-social-link"
        >+ Add social link</Button>

        {/* Iter XX.45 — Partner Agreement gate. */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4 mb-6" data-testid="partner-agreement-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedAgreement}
              onChange={(e) => setAcceptedAgreement(e.target.checked)}
              className="mt-1 w-4 h-4 accent-emerald-500"
              data-testid="partner-agreement-checkbox"
            />
            <div className="text-sm text-neutral-200 leading-relaxed">
              I&apos;ve read and I accept the{" "}
              <Link
                to="/legal/partner_agreement"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:text-emerald-300 underline decoration-dotted"
                data-testid="partner-agreement-link"
              >
                Gully Partner Agreement
              </Link>
              {agreement?.version ? <span className="text-neutral-500"> ({agreement.version})</span> : null}
              {" "}and the{" "}
              <Link
                to="/legal/referral_policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:text-emerald-300 underline decoration-dotted"
                data-testid="partner-referral-policy-link"
              >
                Referral & Commission Policy
              </Link>.
              <div className="text-xs text-neutral-500 mt-1.5">
                Short summary: You own your content. No guaranteed earnings. Commission on eligible Gully Plus revenue only. You can leave anytime.
              </div>
            </div>
          </label>
        </div>

        <Button
          onClick={handlePublish}
          disabled={!canPublish || saving}
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-medium disabled:opacity-40"
          data-testid="partner-publish-btn"
        >
          {saving ? "Publishing…" : (() => {
            const map = {
              coach:      "Get my link and start earning",
              academy:    "Publish and grow my academy",
              creator:    "Get my link and share",
              influencer: "Get my link and start earning",
              club:       "Publish and invite my club",
              school:     "Publish and invite my students",
              league:     "Publish and grow my community",
              other:      "Publish my Gully page",
            };
            return map[partnerType] || "Publish my Gully page";
          })()}
        </Button>
        <div className="text-xs text-neutral-500 text-center mt-3">
          Your public page will be live at askgully.com/@{username || "yourhandle"}
        </div>
      </div>
    </div>
  );
}
